import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm, useFormContext } from 'FrontRoyalReactHookForm';
import { useRootScope } from 'FrontRoyalAngular';
import {
    type CurrentUser,
    ENGLISH_LANGUAGE_PROFICIENCY_DOCUMENTS_TYPES,
    type EnglishLanguageProficiencyDocument,
    MAX_ENGLISH_LANGUAGE_PROFICIENCY_DOCUMENTS,
} from 'Users';
import { navigationHelper } from 'NavigationHelpers';
import transformKeyCase from 'Utils/transformKeyCase';
import { Button } from 'Button';
import { Select } from 'Select';
import { Text } from 'Text';
import { Textarea } from 'Textarea';
import { InlineLink } from 'InlineLink';
import { DOCUMENT_UPLOAD_ACCEPT, useUploadDocument } from 'FrontRoyalUpload';
import { useSaveDocuments } from '../useSaveDocuments';
import { type DocumentsFormValues } from '../editDocuments.types';
import { DocumentUploadButton } from './DocumentUploadButton';
import { LabeledField } from './LabeledField';
import { UploadedDocumentList } from './UploadedDocumentList';

const UPLOAD_URL = `${window.ENDPOINT_ROOT}/api/s3_english_language_proficiency_documents.json`;
const FAQ_PATH = '/help/english-language-requirements';

// Pick a documentation type, upload supporting documents, optionally add comments, then save.
// Replaces the learner-facing half of the old edit-english-documents directive.
//
// No equivalent of the old button's editDocuments.$invalid check: its only rule was the required
// type select, and that select is the only control rendered before a type is chosen, so the form
// can't be dirty without one.
export function EnglishProficiencySection({ currentUser }: { currentUser: CurrentUser }) {
    const { t } = useTranslation('back_royal');
    const { loadFaqUrl } = navigationHelper();
    const { save, saving } = useSaveDocuments();
    const { upload, uploading, errorMessage } = useUploadDocument();

    const methods = useForm<DocumentsFormValues>({
        mode: 'onChange',
        defaultValues: {
            english_language_proficiency_documents_type: currentUser.englishLanguageProficiencyDocumentsType ?? '',
            english_language_proficiency_comments: currentUser.englishLanguageProficiencyComments ?? '',
        },
    });
    const { isDirty } = methods.formState;
    const documentsType = methods.watch('english_language_proficiency_documents_type');

    const typeOptions = ENGLISH_LANGUAGE_PROFICIENCY_DOCUMENTS_TYPES.map(type => ({
        value: type,
        label: t(`settings.edit_documents.${type}`),
    }));

    const onSave = async (values: DocumentsFormValues) => {
        // Only these two fields are permitted for non-admins by users_controller#perform_update.
        // transcripts_verified and english_language_proficiency_documents_approved were in the old
        // directive's EDITABLE_DOCUMENTS_ATTRIBUTES but were sliced off server-side for learners.
        const saved = await save({
            fields: {
                english_language_proficiency_documents_type: values.english_language_proficiency_documents_type,
                english_language_proficiency_comments: values.english_language_proficiency_comments,
            },
            successKey: 'documents_saved',
            // set_user_push_messages echoes these back for a learner, but users_controller derives
            // is_admin from the caller's ability rather than the record being edited, so an admin
            // saving their own record skips that branch entirely. Sync unconditionally, as the old
            // directive's copyAttrs did; for a learner it just rewrites the same values.
            syncToCurrentUser: true,
        });
        if (saved) methods.reset(values);
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSave)} className="flex flex-col gap-4 py-6">
                <Text as="h2" color="black" weight="semibold" className="text-base">
                    {t('settings.edit_documents.english_language_proficiency_documentation')}
                </Text>

                <Text as="p" variant="small">
                    <Trans
                        t={t}
                        i18nKey="settings.edit_documents.english_language_proficiency_documents_desc"
                        components={{
                            faq: <InlineLink className="text-xs" onPress={() => loadFaqUrl(FAQ_PATH, '_blank')} />,
                        }}
                    />
                </Text>

                <DocumentationTypeField
                    isApproved={!!currentUser.englishLanguageProficiencyDocumentsApproved}
                    isDisabled={uploading}
                    options={typeOptions}
                />

                {/* The old template only revealed the uploader and comments once a type was chosen. */}
                {documentsType ? (
                    <EnglishProficiencyUploader
                        currentUser={currentUser}
                        getFormValues={methods.getValues}
                        onUploadPersistedFormValues={values => methods.reset(values)}
                        upload={upload}
                        uploading={uploading}
                        errorMessage={errorMessage}
                    />
                ) : null}

                <div>
                    <InlineLink className="text-xs" onPress={() => loadFaqUrl(FAQ_PATH, '_blank')}>
                        {t('settings.edit_documents.minimum_required_scores')}
                    </InlineLink>
                </div>

                <Button
                    type="submit"
                    color="primary"
                    height={9}
                    fullWidth
                    className="max-w-md"
                    isDisabled={saving || !isDirty}
                >
                    {t('settings.edit_documents.save_documents')}
                </Button>
            </form>
        </FormProvider>
    );
}

// The documentation-type picker. Extracted so the section component stays under the 100-line cap.
function DocumentationTypeField({
    isApproved,
    isDisabled,
    options,
}: {
    isApproved: boolean;
    isDisabled: boolean;
    options: { value: string; label: string }[];
}) {
    const { t } = useTranslation('back_royal');
    const { control } = useFormContext<DocumentsFormValues>();
    const label = t('settings.edit_documents.select_documentation_type');

    return (
        <div className="max-w-md">
            {/* Not marked required once the documents have been approved, matching the old
                template's uploadIsRequired(...) check on the label. */}
            <LabeledField label={label} isRequired={!isApproved}>
                <Controller
                    control={control}
                    name="english_language_proficiency_documents_type"
                    render={({ field }) => (
                        <Select
                            options={options}
                            value={field.value}
                            onValueChange={field.onChange}
                            aria-label={label}
                            placeholder={label}
                            isDisabled={isDisabled}
                            className="w-full justify-between"
                        />
                    )}
                />
            </LabeledField>
        </div>
    );
}

// The uploader plus the comments field, shown once a documentation type is selected.
function EnglishProficiencyUploader({
    currentUser,
    getFormValues,
    onUploadPersistedFormValues,
    upload,
    uploading,
    errorMessage,
}: {
    currentUser: CurrentUser;
    getFormValues: () => DocumentsFormValues;
    onUploadPersistedFormValues: (values: DocumentsFormValues) => void;
    // Owned by the parent so the documentation-type picker can disable itself mid-upload too.
    upload: ReturnType<typeof useUploadDocument>['upload'];
    uploading: boolean;
    errorMessage: string | null;
}) {
    const { t } = useTranslation('back_royal');
    const $rootScope = useRootScope();

    // Uploads happen outside the save button, so the list is local state seeded from the user.
    const [documents, setDocuments] = useState<EnglishLanguageProficiencyDocument[]>(
        () => currentUser.s3EnglishLanguageProficiencyDocuments ?? [],
    );

    const atDocumentLimit = documents.length >= MAX_ENGLISH_LANGUAGE_PROFICIENCY_DOCUMENTS;
    // Comments are read-only once the documents have been approved, matching
    // `documentCommentsAreaIsDisabled` in the old directive.
    const commentsDisabled = currentUser.englishLanguageProficiencyDocumentsApproved;

    const onFileSelected = async (file: File) => {
        // The type and comments ride along with the upload so they are persisted in the same
        // transaction as the document. This is the race-condition fix the deleted
        // edit_english_documents_dir.js carried: navigating away before the response resolved used
        // to leave english_language_proficiency_documents_type unset.
        //
        // Only null/undefined are omitted, matching the old directive's `value != null` filter:
        // multipart form data serializes those as the strings "null"/"undefined". An empty string
        // must still be sent, since that is how the user clears their comments.
        const values = getFormValues();
        const topLevelFields = Object.fromEntries(
            Object.entries({
                english_language_proficiency_documents_type: values.english_language_proficiency_documents_type,
                english_language_proficiency_comments: values.english_language_proficiency_comments,
            }).filter(([, value]) => value != null),
        );

        const uploaded = await upload<EnglishLanguageProficiencyDocument>({
            url: UPLOAD_URL,
            file,
            recordFields: { user_id: currentUser.id },
            topLevelFields,
            responseKey: 's3_english_language_proficiency_documents',
        });
        if (!uploaded) return;

        setDocuments(previous => [...previous, uploaded]);

        // Reflect the new document on the shared currentUser too. The upload endpoint renders the
        // record rather than push messages, so nothing syncs this for us.
        //
        // Snake_cased on the way in, as in TranscriptSection: the user model embedsMany these, so
        // the rest of the array holds snake_cased records and Angular reads them as `file_file_name`.
        const globalUser = $rootScope.currentUser;
        if (globalUser) {
            globalUser.s3_english_language_proficiency_documents = [
                ...(globalUser.s3_english_language_proficiency_documents ?? []),
                transformKeyCase(uploaded, { to: 'snakeCase' }),
            ];
            $rootScope.$applyAsync();
        }

        // The type/comments were saved alongside the document, so the form is clean again.
        onUploadPersistedFormValues(values);
    };

    return (
        <div className="flex flex-col gap-4">
            <DocumentUploadButton
                accept={DOCUMENT_UPLOAD_ACCEPT}
                isDisabled={uploading || atDocumentLimit}
                uploading={uploading}
                errorMessage={errorMessage}
                onFileSelected={file => void onFileSelected(file)}
            >
                {/* Learners see plain file names; the old UI only made these clickable in admin mode. */}
                <UploadedDocumentList documents={documents} />
            </DocumentUploadButton>

            <div className="max-w-md">
                {/* Disabled mid-upload too: the upload persists the comments and then resets the form
                    to the snapshot it took, so anything typed meanwhile would be discarded. */}
                <Textarea
                    name="english_language_proficiency_comments"
                    label={t('settings.edit_documents.comments_optional')}
                    disabled={commentsDisabled || uploading}
                />
            </div>
        </div>
    );
}
